<template>
  <div>
    <div class="mb-6 pb-6 border-b-2 border-grey-light flex flex-wrap items-center">
      <div>
        <h1 class="text-grey-darker text-xl font-semibold leading-normal uppercase">
          {{ page.title }}
        </h1>
        <h2 v-if="stage === null" class="text-grey-dark text-sm font-medium leading-normal">
          {{ page.intro }}
        </h2>
        <h2 v-else @click="stage = null; receipt = null" class="text-grey-dark text-sm font-medium leading-normal cursor-pointer hover:underline">
          &lt;&lt; Back to Packages
        </h2>
      </div>
    </div>

    <loader v-if="!loaded || submittingOrder" />

    <!-- OFFICE HAS NO ACCESS -->
    <panel v-else-if="!officeAccess" class="pt-6">
      <strong class="mb-2">Access Denied</strong>
      <p>Your office does not have access to use this system.</p>
    </panel>

    <!-- RECEIPT -->
    <marketing-pack-new-order-end v-else-if="receipt" :order="order" :receipt="receipt" :priceTotal="priceTotal" :agentProfit="agentProfit" :pack="selectedPack" />

    <!-- PACK SELECTION -->
    <marketing-pack-new-order-start v-else-if="stage === null" :page="page" :packs="packs" :foundation_donation="packs[0].foundation_donation" @start="newOrder($event)" />

    <!-- PACK SELECTED -->
    <panel v-else class="pt-6">
      <div class="flex -mx-6">
        <div class="w-full md:w-2/3 px-6">

          <!-- STAGE TABS -->
          <div class="w-full md:w-2/3 flex text-sm mx-auto mb-6">
            <div @click="stage = i" class="flex flex-1 flex-col items-center cursor-pointer" v-for="(stageName, i) in stages" :key="i" v-if="i === 1 && hasArtworkStage || i !== 1">
              <div class="flex items-center w-full mb-2">
                <div class="border-b flex-grow" :class="i === 0 ? 'border-transparent' : (stage >= i ? 'border-secondary-darker' : 'border-grey')"></div>
                <span class="bg-white border rounded-full w-6 h-6 inline-block flex items-center justify-center" :class="stage > i ? 'border-secondary-darker' : ''">
                  <span class="rounded-full w-3 h-3 inline-block" :class="stage >= i ? 'bg-secondary-darker' : 'bg-grey'"></span>
                </span>
                <div class="border-b flex-grow" :class="i === stages.length-1 ? 'border-transparent' : (stage > i ? 'border-secondary-darker' : 'border-grey')"></div>
              </div>
              {{ stageName }}
            </div>
          </div>

          <div class="bg-grey-lighter border border-b-4 border-red-dark flex p-3 mb-6 rounded sticky pin-t max-h-120 overflow-y-auto z-10" v-if="!isEmpty(errorBag.errors)">
            <i class="fas fa-exclamation-triangle mr-3 fa-2x"></i>
            <div class="flex-grow">
              <strong class="block mb-3">There were problems with your order. Please resolve these issues and try again.</strong>
              <ul class="pl-6">
                <li v-for="(error, i) in errorBag.errors" :key="i">{{ error.join(' ') }}</li>
              </ul>
            </div>
            <i class="fas fa-times ml-3 text-grey-darker hover:text-grey-darkest cursor-pointer" @click="errorBag.clear()"></i>
          </div>

          <!-- STAGE FORM -->
          <component :is="stageView" v-model="order" @updateAddress="updateDeliveryAddress" @changeUpgradeFloorplan="upgradeFloorplan = $event" :page="page" :pack="selectedPack" :associates="associates" :userIsAssociate="userIsAssociate" :deliveryAddresses="deliveryAddresses" :agentProfit="agentProfit" :priceTotal="priceTotal" :ehouse-items="ehouseItems" :focal-agent-items="focalAgentItems" :upgradeFloorplan="upgradeFloorplan" />

          <!-- NEXT BUTTON -->
          <hr class="border-b mt-16" />
          <div class="flex justify-center pt-3">
            <button type="button" @click="stage === 2 && !hasArtworkStage ? stage -= 2 : stage--" v-if="stage > 0" class="bg-grey-light rounded py-3 px-12 text-black mr-3">
              <i class="fas fa-chevron-left"></i> Back
            </button>

            <button type="button" @click="stage === 0 && !hasArtworkStage ? stage += 2 : stage++" v-if="stage < stages.length - 1" class="bg-primary rounded py-3 px-12 text-white">
              {{ stage === stages.length - 2 ? 'Review Order' : 'Next' }} <i class="fas fa-chevron-right"></i>
            </button>

            <button type="button" @click="confirmingOrder = true" v-else class="rounded py-3 px-12" :class="!order.order_confirmation || !order.gdpr_confirmation ? 'bg-grey-light text-grey cursor-not-allowed' : 'bg-primary text-white'" :disabled="!order.order_confirmation || !order.gdpr_confirmation">
              Submit Order <i class="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>

        <!-- ORDER SUMMARY -->
        <div class="w-full md:w-1/3 px-6 border-l text-grey-darkest">
          <h3 class="text-black">Order Summary</h3>
          <table class="w-full mb-6">
            <tbody>
              <tr>
                <td class="align-top pb-3">
                  {{ selectedPack.name }}
                </td>
                <td class="text-right align-top pb-3">
                  {{ !selectedPack.delivery_print ? selectedPack.price_digital : selectedPack.price_print | gbp }}
                </td>
              </tr>

              <tr v-for="(item, i) in selectedItems" :key="i">
                <td class="align-top text-xs" :class="{'line-through text-grey': item.removed}">
                  <ul class="pl-6">
                    <li>{{ item.name }}</li>
                  </ul>
                </td>
                <td class="text-right align-top text-xs">
                  <span v-if="item.removed && item.cost">-</span><span v-if="item.cost">{{ item.cost | gbp }}</span>
                </td>
              </tr>

              <tr v-if="selectedPack.delivery_print && order.delivery_type_id === 1 && selectedPack.price_print - selectedPack.price_digital > 0">
                <td class="align-top py-3">
                  Digital Version Discount
                </td>
                <td class="text-right align-top py-3 text-red">
                  -{{ selectedPack.price_print - selectedPack.price_digital | gbp }}
                </td>
              </tr>

              <tr v-if="ehouseDiscount > 0">
                <td class="align-top py-3">
                  ehouse multi-buy discount
                </td>
                <td class="text-right align-top py-3 text-red">
                  -{{ ehouseDiscount | gbp }}
                </td>
              </tr>
            </tbody>
          </table>

          <hr class="border-b" />

          <!-- TOTAL PRICE -->
          <table class="w-full mb-2">
            <tbody>
              <tr>
                <td class="font-bold pb-1">Subtotal</td>
                <td class="text-right pb-1">{{ priceSubtotal | gbp }}</td>
              </tr>
              <tr>
                <td class="font-bold pb-3">VAT</td>
                <td class="text-right pb-3">{{ priceVAT | gbp }}</td>
              </tr>
              <tr class="text-black font-bold text-sm">
                <td>Total Marketing Pack Price</td>
                <td class="text-right">{{ priceTotal | gbp }}</td>
              </tr>
            </tbody>
          </table>
          <div v-if="refinedOrderItem" class="mt-2 text-xs">Plus Refined Magazine delivery, which will be invoiced seperately</div>
          <div v-else-if="guildPrestigeOrderItem" class="mt-2 text-xs">Plus Guild Magazine delivery, which will be invoiced seperately</div>

          <!-- VENDOR PAYMENT -->
          <div v-if="order.payment_from_vendor && order.payment_amount && order.payment_amount != priceTotal" class="bg-grey-lighter mt-6 -mx-6 p-6">
            <table class="w-full">
              <tbody>
                <tr>
                  <td class="pb-1">To collect from vendor</td>
                  <td class="text-right pb-1">{{ order.payment_amount | gbp }}</td>
                </tr>
                <tr>
                  <td>{{ order.payment_amount > priceTotal ? 'Agent Receives' : 'Agent Pays' }}</td>
                  <td class="text-right font-bold" :class="{'text-green-dark' : order.payment_amount > priceTotal, 'text-red-dark': order.payment_amount < priceTotal}">
                    {{ order.payment_amount > priceTotal ? agentProfit : priceTotal - order.payment_amount | gbp }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </panel>

    <deletion-modal
      v-if="confirmingOrder"
      @confirmed="submitOrder"
      @leave="confirmingOrder = false"
      @dismissed="confirmingOrder = false"
      confirm-label="Submit Order"
      cancel-label="Amend Order">
      <h4 class="font-bold text-lg mb-3">Order Confirmation</h4>
      <div class="bg-grey-lightest p-3 mt-6 flex items-center border">
        <i class="fas fa-info-circle mr-2"></i>
        <span v-if="order.payment_from_vendor && order.associate_id && order.payment_amount < priceTotal">You have chosen for the vendor<span v-if="order.vendor1_name">, {{ order.vendor1_name }},</span> to pay for the Marketing Pack order; this means we will send a payment link via email to the vendor for the payment specified. The vendor payment amount is less than the order total ({{ priceTotal | gbp }}). The selected associate will also receive an email about payment for the remainder. The order will not start until both the associate and vendor payments are complete.</span>
        <span v-if="order.payment_from_vendor && userIsAssociate && order.payment_amount < priceTotal">You have chosen for the vendor<span v-if="order.vendor1_name">, {{ order.vendor1_name }},</span> to pay for the Marketing Pack order; this means we will send a payment link via email to the vendor for the payment specified and the order will not start until the vendor has paid. The vendor payment amount is less than the order total ({{ priceTotal | gbp }}). You will be directed to Stripe Checkout to pay for the remainder.</span>
        <span v-else-if="order.payment_from_vendor">You have chosen for the vendor<span v-if="order.vendor1_name">, {{ order.vendor1_name }},</span> to pay for the Marketing Pack order; this means we will send a payment link via email to the vendor for the payment specified and the order will not start until the vendor has paid.</span>
        <span v-else-if="order.associate_id">You have selected to self-fund the order. We will send a payment link via email to the selected associate for the payment specified and the order will not start until the associate has paid.</span>
        <span v-else-if="userIsAssociate">You have selected to self-fund the order and will be redirected to Stripe Checkout. The order will not start until payment is complete.</span>
        <span v-else>You have selected to self-fund this order, which means we will begin fulfilling the order as soon as possible and you will be invoiced directly for payment. Please be advised that {{ selectedOffice.network_id === 2 ? 'Guild' : 'Fine & Country' }} Marketing Packs are invoiced in full once the first piece of artwork is approved, or 3 months after the date the Marketing Pack is ordered (whichever comes first).</span>
      </div>
    </deletion-modal>
  </div>
</template>

<script>
import api from '@/http'
import { find, isEmpty, filter } from 'lodash'
import ErrorBag from '@/ErrorBag'
import MarketingPackNewOrderStart from './new-order/MarketingPackNewOrderStart'
import MarketingPackNewOrderStage0 from './new-order/MarketingPackNewOrderStage0'
import MarketingPackNewOrderStage1 from './new-order/MarketingPackNewOrderStage1'
import MarketingPackNewOrderStage2 from './new-order/MarketingPackNewOrderStage2'
import MarketingPackNewOrderStage3 from './new-order/MarketingPackNewOrderStage3'
import MarketingPackNewOrderStage4 from './new-order/MarketingPackNewOrderStage4'
import MarketingPackNewOrderEnd from './new-order/MarketingPackNewOrderEnd'
import { mapState } from 'vuex'
import DeletionModal from '../../components/Modals/DeletionModal.vue'
export default {
  props: ['page'],
  components: {
    MarketingPackNewOrderStart,
    MarketingPackNewOrderStage0,
    MarketingPackNewOrderStage1,
    MarketingPackNewOrderStage2,
    MarketingPackNewOrderStage3,
    MarketingPackNewOrderStage4,
    MarketingPackNewOrderEnd,
    DeletionModal
  },
  data () {
    return {
      loaded: false,
      officeAccess: false,
      packs: null,
      associates: null,
      userIsAssociate: null,
      deliveryAddresses: [],
      stages: [
        'Pack Info',
        'Artwork',
        'Add-Ons',
        'Property',
        'Review'
      ],
      stage: null,
      order: null,
      receipt: null,
      errorBag: new ErrorBag(),
      confirmingOrder: false,
      submittingOrder: false,
      upgradeFloorplan: false
    }
  },
  computed: {
    ...mapState(['selectedOffice']),
    propertySizePackItem () {
      return find(this.selectedPack.items, (item) => item.internal_type_id === 0)
    },
    propertySizeOrderItem () {
      return find(this.order.items, (item) => item.marketing_pack_item_id === this.propertySizePackItem.id)
    },
    hasArtworkStage () {
      return find(this.selectedPack.items, (item) => [3, 4, 8].includes(item.internal_type_id)) != null
    },
    refinedPackItem () {
      return find(this.selectedPack.items, (item) => item.internal_type_id === 10)
    },
    refinedOrderItem () {
      if (!this.refinedPackItem) {
        return null
      }
      return find(this.order.items, (item) => item.marketing_pack_item_id === this.refinedPackItem.id)
    },
    guildPrestigePackItem () {
      return find(this.selectedPack.items, (item) => item.internal_type_id === 20)
    },
    guildPrestigeOrderItem () {
      if (!this.guildPrestigePackItem) {
        return null
      }
      return find(this.order.items, (item) => item.marketing_pack_item_id === this.guildPrestigePackItem.id)
    },
    stageView () {
      return `marketing-pack-new-order-stage${this.stage}`
    },
    selectedPack () {
      if (!this.order) {
        return null
      }

      return find(this.packs, pack => pack.id === this.order.marketing_pack_id)
    },
    priceSubtotal () {
      let output = this.order.delivery_type_id === 0 ? this.selectedPack.price_print : this.selectedPack.price_digital

      for (let i = 0; i < this.selectedItems.length; i++) {
        if (!this.selectedItems[i].removed) {
          output += this.selectedItems[i].cost
        } else {
          output -= this.selectedItems[i].cost
        }
      }

      output -= this.ehouseDiscount

      return output
    },
    priceVAT () {
      return this.priceSubtotal * 0.2
    },
    priceTotal () {
      return this.priceSubtotal + this.priceVAT
    },
    agentProfit () {
      if (!this.order.payment_from_vendor) {
        return 0
      }

      return this.order.payment_amount - this.priceTotal
    },
    selectedItems () {
      let output = []

      for (let i = 0; i < this.order.items.length; i++) {
        const packItem = find(this.selectedPack.items, item => item.id === this.order.items[i].marketing_pack_item_id)
        if (packItem.fulfillment_by === 0 || (packItem.internal_type_id === 0 && this.ehouseItems.length === 0 && this.focalAgentItems.length === 0)) {
          continue
        }

        let name = packItem.name
        let cost = packItem.included_in_pack ? 0 : parseFloat(packItem.pivot.price)

        // Focal Agent Photos / Floorplan / EPC
        if (this.propertySizePackItem && this.propertySizeOrderItem && packItem.fulfillment_by === 7 && [11, 19, 1].includes(packItem.internal_type_id)) {
          name += ` (${this.propertySizePackItem.options[this.propertySizeOrderItem.data.id].name})`
          if (packItem.options && packItem.options.length > 0) {
            cost += packItem.options[this.propertySizeOrderItem.data.id].price
          }
        }

        // Sales Particulars
        if (packItem.internal_type_id === 3) {
          name += ` (${packItem.options[this.order.items[i].data.id].name}`
          if (this.order.delivery_type_id === 0) {
            name += ` x${packItem.options[this.order.items[i].data.id].copies} Copies`
          }
          if (packItem.options[this.order.items[i].data.id].styles) {
            name += ` - ${packItem.options[this.order.items[i].data.id].styles[this.order.items[i].data.style_id].name}`
          }
          name += ')'
          cost += packItem.options[this.order.items[i].data.id].price
          if (this.order.items[i].data.additional_copies_id !== null && this.order.items[i].data.additional_copies_id < packItem.options[this.order.items[i].data.id].additional_copies.length) {
            name += ` +${packItem.options[this.order.items[i].data.id].additional_copies[this.order.items[i].data.additional_copies_id].quantity} additional copies`
            cost += packItem.options[this.order.items[i].data.id].additional_copies[this.order.items[i].data.additional_copies_id].price
          }
        }

        // Window Card
        if ([4, 8].includes(packItem.internal_type_id) && this.order.items[i].data.id !== null) {
          name += ` (${packItem.options[this.order.items[i].data.id].name})`
          let additionalCopies = this.order.items[i].data.copies || 1
          let fieldName = this.order.items[i].data.print ? 'price' : 'digital_price'
          cost += (packItem.options[this.order.items[i].data.id][fieldName] || 0) * additionalCopies
        }

        // Property Size / Drone Photos / Drone Video / Professional Floorplan
        if (packItem.internal_type_id === 0 || ([17, 18].includes(packItem.internal_type_id) && packItem.fulfillment_by === 2) || (packItem.internal_type_id === 19 && !packItem.included_in_pack && packItem.options.length > 0)) {
          let itemCost = packItem.options[this.order.items[i].data.id].price
          if (packItem.internal_type_id === 0 && !itemCost) {
            continue
          }
          name += ` (${packItem.options[this.order.items[i].data.id].name})`
          cost += itemCost
        }

        // Presenter
        if (packItem.internal_type_id === 6) {
          let itemCost = packItem.options[this.order.items[i].data.id].price
          if (packItem.options[this.order.items[i].data.id].copies > 1) {
            name += ` x${packItem.options[this.order.items[i].data.id].copies} Copies`
          }
          cost += itemCost
        }

        // Refined Magazine
        if (packItem.internal_type_id === 10) {
          if (this.order.items[i].data.double_page_spread) {
            cost += packItem.options.double_page_spread_cost
            name += ' (Double Page Spread Upgrade)'
          }
        }

        output.push({
          name: name,
          cost: cost,
          removed: false
        })
      }

      let removedIncludedItems = filter(this.selectedPack.items, (packItem) => packItem.included_in_pack && !find(this.order.items, (orderItem) => orderItem.marketing_pack_item_id === packItem.id))
      for (let i = 0; i < removedIncludedItems.length; i++) {
        // Don't show floorplans as a removed item
        if (removedIncludedItems[i].internal_type_id === 19) {
          continue
        }

        let price = parseFloat(removedIncludedItems[i].pivot.price)
        if (removedIncludedItems[i].options && removedIncludedItems[i].options.property_size_savings) {
          price += removedIncludedItems[i].options.property_size_savings[this.propertySizeOrderItem.data.id]
        }

        output.push({
          name: removedIncludedItems[i].name,
          cost: price,
          removed: true
        })
      }

      return output
    },
    ehouseDiscount () {
      let discount = 0

      const photosPackItem = find(this.selectedPack.items, item => item.internal_type_id === 11 && item.fulfillment_by === 2)
      const photosOrderItem = photosPackItem ? find(this.order.items, item => item.marketing_pack_item_id === photosPackItem.id) : null

      const dronePhotosPackItem = find(this.selectedPack.items, item => item.internal_type_id === 17 && item.fulfillment_by === 2)
      const dronePhotosOrderItem = dronePhotosPackItem ? find(this.order.items, item => item.marketing_pack_item_id === dronePhotosPackItem.id) : null

      const droneVideoPackItem = find(this.selectedPack.items, item => item.internal_type_id === 18 && item.fulfillment_by === 2)
      const droneVideoOrderItem = droneVideoPackItem ? find(this.order.items, item => item.marketing_pack_item_id === droneVideoPackItem.id) : null

      if (dronePhotosOrderItem && droneVideoOrderItem) {
        return (dronePhotosPackItem.options[dronePhotosOrderItem.data.id].price - dronePhotosPackItem.options[dronePhotosOrderItem.data.id].price_with_drone_video) + (droneVideoPackItem.options[droneVideoOrderItem.data.id].price - droneVideoPackItem.options[droneVideoOrderItem.data.id].price_with_drone_photos)
      }

      if (dronePhotosOrderItem && photosOrderItem) {
        discount += dronePhotosPackItem.options[dronePhotosOrderItem.data.id].price - dronePhotosPackItem.options[dronePhotosOrderItem.data.id].price_with_photos
      }
      if (droneVideoOrderItem && photosOrderItem) {
        discount += droneVideoPackItem.options[droneVideoOrderItem.data.id].price - droneVideoPackItem.options[droneVideoOrderItem.data.id].price_with_photos
      }

      return discount
    },
    ehouseItems () {
      let output = []

      for (let i = 0; i < this.order.items.length; i++) {
        for (let j = 0; j < this.selectedPack.items.length; j++) {
          if (this.selectedPack.items[j].id === this.order.items[i].marketing_pack_item_id && this.selectedPack.items[j].fulfillment_by === 2) {
            output.push(this.order.items[i].marketing_pack_item_id)
            break
          }
        }
      }

      return output
    },
    focalAgentItems () {
      let output = []

      for (let i = 0; i < this.order.items.length; i++) {
        for (let j = 0; j < this.selectedPack.items.length; j++) {
          if (this.selectedPack.items[j].id === this.order.items[i].marketing_pack_item_id && this.selectedPack.items[j].fulfillment_by === 7 && [11, 19].includes(this.selectedPack.items[j].internal_type_id)) {
            output.push(this.order.items[i].marketing_pack_item_id)
            break
          }
        }
      }

      return output
    }
  },
  created () {
    Promise.all([
      this.loadPacks(),
      this.loadDeliveryAddresses()
    ]).then(() => {
      this.loaded = true
    })
  },
  methods: {
    isEmpty,
    loadPacks () {
      return api.get('marketing-packs', {
        params: {
          office_id: this.selectedOffice.id
        }
      }).then((response) => {
        this.packs = response.data.packs
        this.officeAccess = response.data.access
        this.associates = response.data.associates
        this.userIsAssociate = response.data.user_is_associate
      })
    },
    loadDeliveryAddresses () {
      return api.get('delivery-addresses', {
        params: {
          office_id: this.selectedOffice.id
        }
      }).then((response) => {
        this.deliveryAddresses = response.data
      })
    },
    updateDeliveryAddress ($event) {
      for (let i = 0; i < this.deliveryAddresses.length; i++) {
        if (this.deliveryAddresses[i].id === $event.id) {
          this.deliveryAddresses[i].address = $event.address
        }
      }
    },
    newOrder (packId) {
      const selectedPack = find(this.packs, pack => pack.id === packId)

      let order = {
        marketing_pack_id: packId,
        property_address_name_number: null,
        property_address_line2: null,
        property_address_line3: null,
        property_address_town: null,
        property_address_line4: null,
        property_address_postcode: null,
        vendor1_title: 'Mr',
        vendor1_name: null,
        vendor1_email: null,
        vendor1_phone: null,
        vendor2_title: 'Mr',
        vendor2_name: null,
        vendor2_email: null,
        vendor2_phone: null,
        associate_order: false,
        associate_id: null,
        payment_from_vendor: false,
        payment_amount: null,
        price_qualifier_id: 0,
        guide_price: null,
        bedrooms: 0,
        bathrooms: 0,
        reception_rooms: 0,
        property_type_id: 26,
        delivery_type_id: !selectedPack.delivery_print ? 1 : 0,
        move_reason_id: null,
        interviewer_notes: null,
        notes: null,
        items: [],
        gdpr_confirmation: false,
        order_confirmation: false,
        notification_cc: [],
        supplier_vendor_contact: true,
        tenure_id: null,
        council_tax_band: null,
        epc: null,
        additional_epc: null
      }

      for (let i = 0; i < selectedPack.items.length; i++) {
        if (selectedPack.items[i].included_in_pack || [0, 3].includes(selectedPack.items[i].internal_type_id) || (selectedPack.items[i].internal_type_id === 1 && selectedPack.items[i].fulfillment_by === 0)) {
          let data = null
          // Property Size
          if (selectedPack.items[i].internal_type_id === 0) {
            data = {
              id: 0
            }
          }

          // Sales Particulars
          if (selectedPack.items[i].internal_type_id === 3) {
            // MMN
            if (selectedPack.items[i].fulfillment_by === 8) {
              data = {
                id: 0,
                additional_copies_id: null,
                mmn_template_id: null
              }
            } else {
              data = {
                id: 0,
                additional_copies_id: null,
                style_id: 0
              }
            }
          }

          // Window Card
          if (selectedPack.items[i].internal_type_id === 4) {
            // MMN
            if (selectedPack.items[i].fulfillment_by === 8) {
              data = {
                id: null,
                print: order.delivery_type_id === 0,
                mmn_template_id: null
              }
            } else {
              data = {
                id: null,
                print: order.delivery_type_id === 0
              }
            }
          }

          // Refined
          if (selectedPack.items[i].internal_type_id === 10) {
            data = {
              double_page_spread: selectedPack.items[i].options.double_page_spread_locked_value === true
            }
          }

          // Guild Prestige Magazine
          if (selectedPack.items[i].internal_type_id === 20) {
            data = {
              mmn_template_id: null
            }
          }

          // Presentation Particulars, Drone Photography
          if ([6, 17].includes(selectedPack.items[i].internal_type_id)) {
            data = {
              id: 0
            }
          }

          order.items.push({
            marketing_pack_item_id: selectedPack.items[i].id,
            delivery_address_id: null,
            data: data
          })
        }
      }

      this.order = order
      this.stage = 0
      this.upgradeFloorplan = false
    },
    submitOrder () {
      this.errorBag.clear()
      this.order.office_id = this.selectedOffice.id
      this.confirmingOrder = false
      this.submittingOrder = true

      api.post('/marketing-packs/orders', this.order).then((response) => {
        if (this.userIsAssociate && response.data.checkout_url) {
          window.location = response.data.checkout_url
        } else {
          this.receipt = response.data
          this.submittingOrder = false
        }
      }, (errors) => {
        this.submittingOrder = false
        this.errorBag.store(errors.response.data)
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.max-h-120 {
  max-height: 120px;
}
</style>
